/**
 * Teacher Portal — public customer-facing UI (v6.32.84 — Legal Terms Gate).
 *
 * Routes:
 *   /teacher-portal/login   →  TeacherPortalLogin
 *   /teacher-portal         →  TeacherPortalHome (auth-gated)
 *
 * Strict isolation: tokens for this portal are stored under a SEPARATE
 * localStorage key (`mosaic_portal_token`) so they never collide with
 * the main MOSAIC session token.
 *
 * v6.32.84: First-login flow is now a 2-step gate:
 *   1. FirstLoginGate          — forced password change
 *   2. LegalTermsGate (NEW)    — binding acceptance of MOSAIC legal terms
 * Each gate corresponds to a distinct backend 403 reason on /auth/me:
 *   `password_change_required`  → step 1
 *   `terms_acceptance_required` → step 2
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  GraduationCap,
  Lock,
  LogOut,
  Download,
  BookOpen,
  Loader2,
  ShieldCheck,
  AlertTriangle,
  Mail,
  Printer,
  Eye,
  EyeOff,
  Scale,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PORTAL_TOKEN_KEY = 'mosaic_portal_token';

const portalAuthHeaders = () => {
  const t = localStorage.getItem(PORTAL_TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return iso; }
};

// =========================================================================
// LOGIN PAGE  /teacher-portal/login
// =========================================================================
export function TeacherPortalLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // v6.32.85 — self-service forgot-password magic link.
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // If already signed in, send to portal home.
  useEffect(() => {
    const t = localStorage.getItem(PORTAL_TOKEN_KEY);
    if (t) navigate('/teacher-portal', { replace: true });
  }, [navigate]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !password) {
      toast.error('Enter both email and password.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/teacher-portal/auth/login`,
        { email: email.trim().toLowerCase(), password },
        { headers: { 'Content-Type': 'application/json' } }
      );
      localStorage.setItem(PORTAL_TOKEN_KEY, data.session_token);
      navigate('/teacher-portal', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error('Sign-in failed', { description: typeof detail === 'string' ? detail : 'Please check your email and password.' });
    } finally {
      setSubmitting(false);
    }
  };

  const submitForgot = async (e) => {
    e?.preventDefault?.();
    const addr = (forgotEmail || email).trim().toLowerCase();
    if (!addr || !/.+@.+\..+/.test(addr)) {
      toast.error('Enter the email you use to sign in.');
      return;
    }
    setForgotSubmitting(true);
    try {
      await axios.post(
        `${API}/api/teacher-portal/auth/forgot-password`,
        { email: addr },
        { headers: { 'Content-Type': 'application/json' } }
      );
      setForgotSent(true);
    } catch {
      // Even on backend error we show the generic "sent" UI so there
      // is no account-enumeration side channel.
      setForgotSent(true);
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4 py-10" data-testid="teacher-portal-login">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-[#002FA7] text-white mb-4">
            <GraduationCap size={24} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>MOSAIC Teacher Portal</h1>
          <p className="text-sm text-gray-500 mt-1">Print-only access for teachers and school print rooms.</p>
        </div>

        <form onSubmit={submit} className="bg-white border border-gray-200 px-6 py-7 space-y-4 shadow-sm">
          <div>
            <label className="text-xs font-semibold block mb-1.5 text-gray-700">Email (your username)</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ms.garcia@schoolemail.edu"
              autoComplete="email"
              autoFocus
              className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]"
              data-testid="portal-login-email"
            />
          </div>
          <div>
            <label className="text-xs font-semibold block mb-1.5 text-gray-700">Password</label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full border border-gray-300 px-3 py-2 pr-9 text-sm focus:outline-none focus:border-[#002FA7]"
                data-testid="portal-login-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1">First-time sign-in? Use the temporary password from your welcome email — you'll be asked to choose a new one.</p>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#002FA7] hover:bg-blue-800 disabled:opacity-60 text-white font-semibold py-2.5 transition-colors"
            data-testid="portal-login-submit"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
          <div className="text-center pt-1">
            <button
              type="button"
              onClick={() => { setForgotEmail(email); setShowForgot(true); setForgotSent(false); }}
              className="text-xs text-[#002FA7] hover:underline"
              data-testid="portal-forgot-password-link"
            >
              Forgot your password?
            </button>
          </div>
        </form>

        <p className="text-[11px] text-gray-400 text-center mt-6 leading-relaxed">
          This portal is for downloading and printing MOSAIC assessment materials only.
          For other MOSAIC features, sign in at <a href="/login" className="text-[#002FA7] hover:underline">the main login page</a>.
        </p>
      </div>

      {/* v6.32.85 — Forgot Password modal */}
      {showForgot && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          data-testid="portal-forgot-password-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowForgot(false); }}
        >
          <form
            onSubmit={submitForgot}
            className="bg-white w-full max-w-md border border-gray-200 px-6 py-7 space-y-4"
          >
            <div className="flex items-center gap-2">
              <Mail size={18} className="text-[#002FA7]" />
              <h2 className="text-lg font-bold text-gray-900">Reset your password</h2>
            </div>
            {forgotSent ? (
              <>
                <p className="text-sm text-gray-600" data-testid="portal-forgot-password-sent-msg">
                  If an account exists for that email, we've sent a password-reset
                  link. The link expires in <b>60 minutes</b> and can be used once.
                </p>
                <p className="text-xs text-gray-500">
                  Check your inbox (and spam folder). Didn't get it? Email
                  {' '}<a href="mailto:support@mosaicassessmentco.com" className="text-[#002FA7]">support@mosaicassessmentco.com</a>.
                </p>
                <button
                  type="button"
                  onClick={() => setShowForgot(false)}
                  className="w-full bg-[#002FA7] hover:bg-blue-800 text-white font-semibold py-2.5"
                  data-testid="portal-forgot-password-close"
                >
                  Close
                </button>
              </>
            ) : (
              <>
                <p className="text-xs text-gray-500">
                  Enter the email you use to sign in. We'll email you a one-time
                  link to choose a new password.
                </p>
                <div>
                  <label className="text-xs font-semibold block mb-1.5 text-gray-700">Email</label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="ms.garcia@schoolemail.edu"
                    autoComplete="email"
                    autoFocus
                    className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]"
                    data-testid="portal-forgot-password-email"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowForgot(false)}
                    className="flex-1 border border-gray-300 bg-white text-gray-700 font-semibold py-2.5"
                    data-testid="portal-forgot-password-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={forgotSubmitting}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#002FA7] hover:bg-blue-800 disabled:opacity-60 text-white font-semibold py-2.5"
                    data-testid="portal-forgot-password-submit"
                  >
                    {forgotSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                    {forgotSubmitting ? 'Sending…' : 'Send reset link'}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      )}
    </div>
  );
}

// =========================================================================
// RESET PASSWORD PAGE  /teacher-portal/reset-password?token=...
// v6.32.85 — Consumes a one-time password-reset token (sent via email).
// =========================================================================
export function TeacherPortalResetPassword() {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const t = (sp.get('token') || '').trim();
      if (t) setToken(t);
      else setError('This reset link is missing its token. Please request a new one.');
    } catch { /* no-op */ }
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!token) { setError('Missing reset token.'); return; }
    if (newPwd.length < 10) {
      toast.error('Password must be at least 10 characters.');
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/teacher-portal/auth/reset-password`,
        { token, new_password: newPwd },
        { headers: { 'Content-Type': 'application/json' } }
      );
      setSuccess(true);
      // Clear any stale portal session (reset always requires re-login).
      try { localStorage.removeItem(PORTAL_TOKEN_KEY); } catch { /* no-op */ }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not reset your password. Please request a new link.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4 py-10" data-testid="teacher-portal-reset-password">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-[#002FA7] text-white mb-4">
            <ShieldCheck size={24} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Reset your password</h1>
          <p className="text-sm text-gray-500 mt-1">MOSAIC Teacher Portal</p>
        </div>

        {success ? (
          <div className="bg-white border border-gray-200 px-6 py-7 space-y-4 shadow-sm" data-testid="portal-reset-password-success">
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-emerald-600" />
              <h2 className="text-lg font-bold text-gray-900">Password updated</h2>
            </div>
            <p className="text-sm text-gray-600">
              Your new password is now active. Please sign in with your email and
              new password to continue.
            </p>
            <button
              type="button"
              onClick={() => navigate('/teacher-portal/login', { replace: true })}
              className="w-full bg-[#002FA7] hover:bg-blue-800 text-white font-semibold py-2.5"
              data-testid="portal-reset-password-signin"
            >
              Go to sign-in
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-gray-200 px-6 py-7 space-y-4 shadow-sm">
            {error ? (
              <div className="bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700" data-testid="portal-reset-password-error">
                {error}
              </div>
            ) : null}
            <div>
              <label className="text-xs font-semibold block mb-1.5 text-gray-700">New password</label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  className="w-full border border-gray-300 px-3 py-2 pr-9 text-sm focus:outline-none focus:border-[#002FA7]"
                  data-testid="portal-reset-password-new"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Minimum 10 characters.</p>
            </div>
            <div>
              <label className="text-xs font-semibold block mb-1.5 text-gray-700">Confirm new password</label>
              <input
                type={showPwd ? 'text' : 'password'}
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                autoComplete="new-password"
                className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]"
                data-testid="portal-reset-password-confirm"
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !token}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#002FA7] hover:bg-blue-800 disabled:opacity-60 text-white font-semibold py-2.5"
              data-testid="portal-reset-password-submit"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
              {submitting ? 'Updating…' : 'Set new password'}
            </button>
            <p className="text-[11px] text-gray-500 text-center">
              Link not working? <a href="/teacher-portal/login" className="text-[#002FA7] hover:underline">Request a new one</a>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// FIRST-LOGIN PASSWORD-CHANGE GATE (modal — blocks the home page)
// =========================================================================
function FirstLoginGate({ onChanged }) {
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (newPwd.length < 10) {
      toast.error('New password must be at least 10 characters.');
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error('New password and confirmation do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/teacher-portal/auth/change-password`,
        { current_password: currentPwd, new_password: newPwd },
        { headers: { ...portalAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      toast.success('Password updated.');
      onChanged();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error('Could not change password', { description: typeof detail === 'string' ? detail : undefined });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="portal-first-login-gate">
      <form onSubmit={submit} className="bg-white w-full max-w-md border border-gray-200 px-6 py-7 space-y-4">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="text-[#002FA7]" />
          <h2 className="text-lg font-bold text-gray-900">Choose a new password</h2>
        </div>
        <p className="text-xs text-gray-500">
          For your security, you must change the temporary password before continuing.
          New password must be at least 10 characters.
        </p>
        <div>
          <label className="text-xs font-semibold block mb-1">Temporary password (from your welcome email)</label>
          <input type="password" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} autoComplete="current-password" className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]" data-testid="portal-current-pwd" />
        </div>
        <div>
          <label className="text-xs font-semibold block mb-1">New password</label>
          <input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} autoComplete="new-password" className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]" data-testid="portal-new-pwd" />
        </div>
        <div>
          <label className="text-xs font-semibold block mb-1">Confirm new password</label>
          <input type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} autoComplete="new-password" className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]" data-testid="portal-confirm-pwd" />
        </div>
        <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-[#002FA7] hover:bg-blue-800 disabled:opacity-60 text-white font-semibold py-2.5">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
          {submitting ? 'Updating…' : 'Save new password'}
        </button>
      </form>
    </div>
  );
}

// =========================================================================
// LEGAL TERMS ACCEPTANCE GATE (modal — blocks the home page)
// Shown after FirstLoginGate. Backend signals via 403 detail.reason =
// 'terms_acceptance_required' on any portal route except
// /auth/legal-terms and /auth/accept-terms.
// =========================================================================
function LegalTermsGate({ onAccepted, onSignOut }) {
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/teacher-portal/legal-terms`,
          { headers: portalAuthHeaders() }
        );
        if (!cancelled) setTerms(data);
      } catch {
        if (!cancelled) toast.error('Could not load legal terms');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!agreed) {
      toast.error('Please check the agreement box to continue.');
      return;
    }
    if (!terms?.version) {
      toast.error('Legal terms not loaded yet — please try again.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/teacher-portal/auth/accept-terms`,
        { terms_version: terms.version, accepted: true },
        { headers: { ...portalAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      toast.success('Terms accepted. Welcome to MOSAIC.');
      onAccepted();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error('Could not record acceptance', { description: typeof detail === 'string' ? detail : undefined });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="portal-legal-terms-gate">
      <form
        onSubmit={submit}
        className="bg-white w-full max-w-2xl border border-gray-200 max-h-[90vh] flex flex-col"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2 bg-amber-50">
          <Scale size={18} className="text-amber-700" />
          <h2 className="text-base font-bold text-gray-900">
            {terms?.heading || 'Important — Legal terms of access'}
          </h2>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1" data-testid="portal-legal-terms-body">
          {loading ? (
            <div className="text-sm text-gray-400 text-center py-8">Loading legal terms…</div>
          ) : !terms ? (
            <div className="text-sm text-red-600 text-center py-8">Could not load legal terms. Please sign out and try again.</div>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                Before you can access any MOSAIC assessment materials, please
                read and accept the following legal terms. This acceptance is
                logged with your account, IP address, and a timestamp as a
                binding record of agreement.
              </p>
              <ul className="space-y-3 text-sm text-gray-800 leading-relaxed list-disc pl-5" data-testid="portal-legal-terms-list">
                {(terms.bullets || []).map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
              <p className="text-[11px] text-gray-400 mt-4">
                Terms version: <span className="font-mono">{terms.version}</span>
              </p>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-slate-50 space-y-3">
          <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={loading || !terms}
              className="mt-1"
              data-testid="portal-legal-terms-checkbox"
            />
            <span>
              I have read, understood, and agree to the legal terms above.
              I understand that unauthorized reproduction, distribution, or
              credential sharing may result in immediate revocation of access,
              civil damages, and criminal prosecution.
            </span>
          </label>
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={onSignOut}
              className="px-3 py-2 text-xs font-medium border border-gray-200 hover:bg-gray-100"
              data-testid="portal-legal-terms-decline"
            >
              I do not agree — sign out
            </button>
            <button
              type="submit"
              disabled={!agreed || submitting || loading || !terms}
              className="inline-flex items-center justify-center gap-2 bg-[#002FA7] hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-5 py-2 text-sm"
              data-testid="portal-legal-terms-accept"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
              {submitting ? 'Recording…' : 'I Agree — Continue'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// =========================================================================
// HOME / PORTAL PAGE  /teacher-portal
// =========================================================================
export function TeacherPortalHome() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [meErr, setMeErr] = useState(null);
  const [needsPwdChange, setNeedsPwdChange] = useState(false);
  const [needsTermsAccept, setNeedsTermsAccept] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/api/teacher-portal/auth/logout`, null, { headers: portalAuthHeaders() }); } catch { /* ignore */ }
    localStorage.removeItem(PORTAL_TOKEN_KEY);
    navigate('/teacher-portal/login', { replace: true });
  }, [navigate]);

  // Fetch /me. 403 with reason=password_change_required → show pwd gate.
  // 403 with reason=terms_acceptance_required → show legal terms gate.
  const fetchMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/teacher-portal/auth/me`, { headers: portalAuthHeaders() });
      setMe(data);
      setNeedsPwdChange(false);
      setNeedsTermsAccept(false);
      setMeErr(null);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const reason = typeof detail === 'object' && detail !== null ? detail.reason : null;
      if (status === 403 && reason === 'password_change_required') {
        setNeedsPwdChange(true);
        setNeedsTermsAccept(false);
        setMeErr(null);
      } else if (status === 403 && reason === 'terms_acceptance_required') {
        setNeedsPwdChange(false);
        setNeedsTermsAccept(true);
        setMeErr(null);
      } else {
        // 401 / expired / revoked — kick to login
        localStorage.removeItem(PORTAL_TOKEN_KEY);
        setMeErr(typeof detail === 'string' ? detail : 'Session expired. Please sign in again.');
        navigate('/teacher-portal/login', { replace: true });
      }
    }
  }, [navigate]);

  const fetchCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    try {
      const { data } = await axios.get(`${API}/api/teacher-portal/catalog`, { headers: portalAuthHeaders() });
      setCatalog(data.catalog || []);
    } catch {
      toast.error('Could not load print catalog');
    } finally {
      setLoadingCatalog(false);
    }
  }, []);

  // On first mount: ensure we have a token, then fetch /me.
  useEffect(() => {
    const t = localStorage.getItem(PORTAL_TOKEN_KEY);
    if (!t) {
      navigate('/teacher-portal/login', { replace: true });
      return;
    }
    fetchMe();
  }, [fetchMe, navigate]);

  // Load catalog only AFTER /me succeeds (i.e. password + terms done).
  useEffect(() => {
    if (me && !needsPwdChange && !needsTermsAccept) fetchCatalog();
  }, [me, needsPwdChange, needsTermsAccept, fetchCatalog]);

  const onDownload = async (link, filename) => {
    setDownloading(filename);
    try {
      const resp = await axios.get(`${API}${link}`, { headers: portalAuthHeaders(), responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error('Download failed', { description: typeof detail === 'string' ? detail : 'Please try again or contact your administrator.' });
    } finally {
      setDownloading(null);
    }
  };

  const expiringSoon = useMemo(() => {
    if (!me?.expires_at) return false;
    try {
      const d = new Date(me.expires_at);
      const days = (d.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
      return days > 0 && days <= 14;
    } catch { return false; }
  }, [me]);

  if (meErr) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-gray-500" data-testid="portal-error">{meErr}</div>
    );
  }

  if (!me && !needsPwdChange && !needsTermsAccept) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-gray-400" data-testid="portal-loading-me">Loading your portal…</div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="teacher-portal-home">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#002FA7] text-white inline-flex items-center justify-center"><GraduationCap size={18} /></div>
            <div>
              <div className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>MOSAIC Teacher Portal</div>
              <div className="text-[11px] text-gray-500">Print-ready Student Booklets, Teacher Keys &amp; Toolkit</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {me && (
              <div className="text-right hidden md:block">
                <div className="text-xs font-semibold text-gray-700">{me.teacher_name || me.email}</div>
                <div className="text-[10px] text-gray-400">{me.school_name || `Order ${me.order_number || '—'}`}</div>
              </div>
            )}
            <button onClick={logout} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 hover:bg-gray-50" data-testid="portal-logout">
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </header>

      {/* First-login gate */}
      {needsPwdChange && (
        <FirstLoginGate onChanged={() => { setNeedsPwdChange(false); fetchMe(); }} />
      )}

      {/* Legal terms acceptance gate (after password change) */}
      {!needsPwdChange && needsTermsAccept && (
        <LegalTermsGate
          onAccepted={() => { setNeedsTermsAccept(false); fetchMe(); }}
          onSignOut={logout}
        />
      )}

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {me && (
          <>
            {/* Welcome strip */}
            <div className="bg-white border border-gray-200 px-5 py-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Welcome back</div>
                <div className="text-lg font-bold text-gray-900">{me.teacher_name || me.email}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Order <span className="font-mono">{me.order_number || '—'}</span> &middot; {me.school_name || 'No school on file'} &middot;
                  Access expires <strong>{fmtDate(me.expires_at)}</strong>
                </div>
              </div>
              <a href="mailto:support@mosaicassessmentco.com" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 hover:bg-gray-50" data-testid="portal-contact-support">
                <Mail size={12} /> Contact MOSAIC support
              </a>
            </div>

            {expiringSoon && (
              <div className="bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-2 text-amber-800 text-sm" data-testid="portal-expiring-soon">
                <AlertTriangle size={16} className="mt-0.5" />
                <div>
                  Your access expires in less than 14 days ({fmtDate(me.expires_at)}). Please finish your downloads —
                  contact your MOSAIC administrator if you need an extension.
                </div>
              </div>
            )}
          </>
        )}

        {/* Catalog */}
        {!needsPwdChange && !needsTermsAccept && (
          <section data-testid="portal-catalog-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2"><Printer size={16} /> Print materials</h2>
              <span className="text-[11px] text-gray-400">All PDFs are PDF/X-1a, 300 DPI grayscale, with crop marks &amp; bleed.</span>
            </div>
            {loadingCatalog ? (
              <div className="bg-white border border-gray-200 px-5 py-12 text-center text-gray-400 text-sm" data-testid="portal-catalog-loading">Loading catalog…</div>
            ) : catalog.length === 0 ? (
              <div className="bg-white border border-gray-200 px-5 py-12 text-center text-gray-500 text-sm" data-testid="portal-catalog-empty">
                No print materials are available for your account yet. Please contact your MOSAIC administrator.
              </div>
            ) : (
              <div className="space-y-4">
                {catalog.map((g) => (
                  <div key={g.grade} className="bg-white border border-gray-200" data-testid={`portal-grade-${g.grade}`}>
                    <div className="px-4 py-2.5 bg-slate-50 border-b border-gray-200">
                      <div className="text-sm font-bold text-gray-900">{g.grade_label}</div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {g.modalities.map((m) => (
                        <div key={m.modality} className="px-4 py-3" data-testid={`portal-modality-${g.grade}-${m.modality}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <BookOpen size={13} className="text-[#002FA7]" />
                              <span className="text-sm font-semibold text-gray-800">{m.modality_label}</span>
                              <span className="text-[10px] text-gray-400">({m.question_count} questions)</span>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {Object.entries(m.download_links).map(([key, link]) => {
                              const filename = link.split('/').pop();
                              const [bt, tier] = key.split('_'); // student / teacher  +  A | BC | ALL
                              const tierLabel = tier === 'A' ? 'Tier A (Levels 1–3)' : tier === 'BC' ? 'Tier B/C (Levels 3–6)' : 'All levels';
                              const isStudent = bt === 'student';
                              const downloadingThis = downloading === filename;
                              return (
                                <button
                                  key={key}
                                  onClick={() => onDownload(link, filename)}
                                  disabled={!!downloading}
                                  className={`flex items-center justify-between gap-2 px-3 py-2 text-xs border transition-colors text-left disabled:opacity-50 ${isStudent ? 'border-blue-200 bg-blue-50 hover:bg-blue-100 text-[#002FA7]' : 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800'}`}
                                  data-testid={`portal-download-${g.grade}-${m.modality}-${key}`}
                                >
                                  <span>
                                    <div className="font-bold">{isStudent ? 'Student Booklet' : 'Teacher Key'}</div>
                                    <div className="text-[10px] opacity-80">{tierLabel}</div>
                                  </span>
                                  {downloadingThis ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </main>

      <footer className="max-w-6xl mx-auto px-6 py-8 text-center text-[11px] text-gray-400">
        MOSAIC Assessment Co. &middot; Mosaic Language Proficiency Assessment &middot; Print-only Teacher Portal
      </footer>
    </div>
  );
}

export default TeacherPortalHome;
